(ns common.schema.entities.workspace.kyc.templates.capman.natural-person
  (:require
   [common.schema.entities.workspace.kyc.kyc-options :as kyc-options]
   [common.schema.entities.workspace.kyc.kyc-utils :as kyc-utils]
   [common.schema.entities.workspace.kyc.questions.questions-registry :as questions-registry]
   [common.schema.entities.workspace.kyc.templates.capman.company-config :as company-config]
   [common.schema.entities.workspace.kyc.templates.capman.pep :as pep]
   [common.schema.entities.workspace.kyc.templates.capman.ubo :as ubo]))

(def mandatory-field-meta
  {:mandatory-field? true})

;; Natural Person only accepts passport or national ID card — there is no
;; "previous steps" attachment for an individual investor (per DEV-8071).
;; The legacy `:provided-in-previous-questions` value is kept here as
;; `:deprecated?` so existing answers render disabled (forcing the responder
;; to re-select a real identification type) and are stripped on answers-copy.
(def capman-signer-identification-options
  (concat kyc-options/identification-options
          [{:key          :identification.type/provided-in-previous-questions
            :ord          3.0
            :deprecated?  true
            :translations {:en "ID attached in previous steps"
                           :fi "Liitetty aiemmassa kohdassa lomakkeelle"}}]))

(def schema
  {:kyc-template/template-name
   "Natural person"

   :kyc-template/kyc-form-preface
   company-config/form-preface-text

   :kyc-template/svg-logo
   company-config/svg-logo

   :kyc-template/png-logo
   company-config/png-logo

   :kyc-template/help-email-address
   company-config/contact-email

   :kyc-template/kyc-form-title
   {:translations {:en "Know Your Customer (KYC) form"
                   :fi "Asiakastietokysely"}}

   :kyc.natural-person/basic-information
   {:ord          1.0
    :translations {:en "Basic information"
                   :fi "Perustiedot"}
    :pdf-columns  2
    :questions    (questions-registry/resolve-questions
                   [[:kyc-person/first-name
                     (merge mandatory-field-meta
                            {:translations {:en "First name"
                                            :fi "Etunimi"}})]
                    [:kyc-person/middle-names]
                    [:kyc-person/last-name mandatory-field-meta]
                    [:kyc-person/nationalities
                     {:mandatory-field? (fn [answers _kyc-process]
                                          (kyc-utils/nationalities-unanswered?
                                           answers
                                           kyc-utils/person-nationality-keys))}]
                    [:kyc-person/dob mandatory-field-meta]
                    [:kyc-person/pob mandatory-field-meta]
                    [:kyc-person/address mandatory-field-meta]
                    [:kyc-person/zip-code mandatory-field-meta]
                    [:kyc-person/city mandatory-field-meta]
                    [:kyc-person/country mandatory-field-meta]
                    [:kyc-person/email mandatory-field-meta]
                    [:kyc-person/phone mandatory-field-meta]
                    [:kyc-person/occupation mandatory-field-meta]
                    [:kyc-person/actual-line-of-business]
                    [:kyc-person/size-of-investment-portfolio
                     {:mandatory-field? true
                      :translations     {:en "Size of investor's investment portfolio (EUR)"
                                         :fi "Sijoitusvarallisuuden kokonaisarvo (EUR)"}
                      :info             {:en "Please give an estimate of the total value of your investment portfolio"
                                         :fi "Suuntaa-antava arvio sijoitusvarallisuutesi kokonaisarvosta."}
                      :info-position    :below-input}]
                    [:kyc-person/source-of-funds
                     {:mandatory-field? true
                      :info             {:en "Source of funds means the origin of the funds invested. It includes the activity and means that generated the funds used in investments."
                                         :fi "Varojen alkuperällä tarkoitetaan sijoitettavien varojen alkuperää. Määritelmään sisältyy toiminta ja keinot, jotka ovat tuottaneet sijoitettavat varat."}
                      :info-position    :below-label}]
                    [:kyc-person/source-of-funds-salaries-amount
                     {:mandatory-field? (fn [answers]
                                          (some #{:source-of-funds/salaries}
                                                (:kyc-person/source-of-funds answers)))
                      :translations     {:en "Annual salary circa"
                                         :fi "Vuosipalkka (suuntaa-antava)"}}]
                    [:kyc-person/source-of-funds-capital-income-amount
                     {:mandatory-field? (fn [answers]
                                          (some #{:source-of-funds/capital-income}
                                                (:kyc-person/source-of-funds answers)))
                      :translations     {:en "Annual capital income circa"
                                         :fi "Vuosittainen pääomatulo (suuntaa-antava)"}}]
                    [:kyc-person/source-of-funds-inheritance-amount
                     {:mandatory-field? (fn [answers]
                                          (some #{:source-of-funds/inheritance}
                                                (:kyc-person/source-of-funds answers)))
                      :translations     {:en "Inheritance amount circa"
                                         :fi "Perinnön määrä (suuntaa-antava)"}}]
                    [:kyc-person/source-of-funds-other-amount
                     {:mandatory-field? (fn [answers]
                                          (some #{:source-of-funds/other}
                                                (:kyc-person/source-of-funds answers)))
                      :translations     {:en "Other source, amount circa"
                                         :fi "Muiden varojen määrä (suuntaa-antava)"}}]
                    [:kyc-person/source-of-funds-other-specify
                     {:mandatory-field? (fn [answers]
                                          (some #{:source-of-funds/other}
                                                (:kyc-person/source-of-funds answers)))
                      :translations     {:en "Other source"
                                         :fi "Muiden varojen alkuperä"}}]
                    [:kyc-person/source-of-wealth
                     {:mandatory-field? true
                      :info             {:en "Source of wealth means the origin of total wealth."
                                         :fi "Varallisuuden alkuperällä tarkoitetaan kokonaisvarallisuuden alkuperää."}
                      :info-position    :below-input}]
                    [:kyc-person/other-person-investment? mandatory-field-meta]
                    [:kyc-person/ubo-edn {:data/template ubo/schema}]])}

   :kyc.natural-person/pep-information
   {:ord
    2.0
    :translations
    {:en "Politically exposed person (PEP)"
     :fi "Poliittinen vaikutusvalta (PEP)"}
    :info
    {:en
     (str
      "<p>A Politically Exposed Person (PEP) is an individual who currently holds or has "
      "during the past 12 months held a prominent public function, or is an immediate family "
      "member or close associate of such an individual.</p>")

     :fi
     (str
      "<p>Poliittisesti vaikutusvaltainen henkilö (PEP) on henkilö, joka tällä hetkellä on tai "
      "on viimeisen 12 kuukauden aikana ollut merkittävässä julkisessa tehtävässä, tai "
      "on tällaisen henkilön perheenjäsen tai läheinen yhteistyökumppani.</p>")}
    :more-info
    {:link-text   {:en "More information"
                   :fi "Lisätietoa täällä"}
     :info-header {:en "Politically exposed person (PEP)"
                   :fi "Poliittinen vaikutusvalta (PEP)"}
     :info-content
     {:en
      (str
       "<p>Politically Exposed Person <strong>(“PEP”)</strong> refers to a person who is or "
       "during <strong>the last 12 months</strong> "
       "has been in a prominent public function, a close associate or an immediate family member "
       "of such a person either in Finland or another country:</p>"
       "<p><strong>A prominent public function is acting as:</strong>"
       "<ul><li>Head of State, Head of Government, minister, deputy or assistant minister</li>"
       "<li>Member of Parliament</li>"
       "<li>member of supreme court, member of constitutional court or member of some "
       "corresponding judicial body whose decisions are not generally subject to further appeal</li>"
       "<li>member of court of auditors or of the highest decision-making body of an "
       "authority auditing the management of Government funds and corresponding to the "
       "State Audit Office</li>"
       "<li>member of the Board of a central bank</li>"
       "<li>ambassador or chargé d’affaires</li>"
       "<li>high-ranking officer (minimum rank of general) in the armed forces</li>"
       "<li>member of the administrative, management or supervisory body of a "
       "state-owned company</li>"
       "<li>Member of the EU Parliament</li>"
       "<li>commissioner</li>"
       "<li>Member of the EU Court of Justice</li>"
       "<li>Member of the EU Court of Auditors</li></ul></p>"
       "<p><strong>The associates of politically exposed persons are:</strong>"
       "<ul><li>Any natural person who is known to have joint beneficial ownership of a "
       "corporation, entrepreneur or legal arrangement, or any other close business relations, "
       "with a politically exposed person</li>"
       "<li>Any natural person who has sole beneficial ownership of a corporation, "
       "entrepreneur or legal arrangement which is known to have been set up for the benefit "
       "of a politically exposed person</li></ul></p>"
       "<p><strong>The immediate family members of politically exposed persons are:</strong>"
       "<ul><li>The spouse or any partner considered equivalent to a spouse by national law;</li>"
       "<li>The children and their spouses or partners referred to above; and</li>"
       "<li>The parents.</li></ul></p>")
      :fi
      (str
       "<p>Poliittisesti vaikutusvaltainen henkilö <strong>(“PEP”)</strong> toimii tai on toiminut "
       "merkittävässä julkisessa virassa, kuuluu tai on kuulunut sellaisen henkilön lähipiiriin "
       "tai on tai on ollut sellaisen henkilön perheenjäsen tai lähipiiriin kuuluva, Suomessa "
       "tai toisessa valtiossa <strong>viimeisen 12 kuukauden aikana</strong>:</p>"
       "<p><strong>Merkittävänä poliittisena virkana pidetään seuraavia virkoja:</strong>"
       "<ul><li>Valtion- tai hallituksen päämies, ministeri, vara-tai apulaisministeri</li>"
       "<li>Parlamentin jäsen</li>"
       "<li>Korkeimman oikeuden, perustuslakituomioistuimen tai muun vastaavan oikeuselimen jäsen</li>"
       "<li>Keskuspankin johtokunnan jäsen</li>"
       "<li>Suurlähettiläs</li>"
       "<li>Puolustusvoimien kenraalikuntaan kuuluva upseeri</li>"
       "<li>Valtionyritysten hallinto-, johto- ja valvontaelimen jäsen</li>"
       "<li>EU-parlamentin jäsen</li>"
       "<li>Komission jäsen</li>"
       "<li>EU-tuomioistuimen jäsen</li>"
       "<li>EU:n tilintarkastustuomioistuimen jäsen</li></ul></p>"
       "<p><strong>Poliittisesti vaikutusvaltaisen henkilön lähipiiriin kuuluvaksi katsotaan:</strong>"
       "<ul><li>Luonnollinen henkilö, joka toimii poliittisesti vaikutusvaltaisen henkilön "
       "yhtiökumppanina</li>"
       "<li>Luonnollinen henkilö, jonka omistaman yhtiön tosiasiallisena edunsaajana on "
       "poliittisesti vaikutusvaltainen henkilö.</li></ul></p>"
       "<p><strong>Poliittisesti vaikutusvaltaisen henkilön lähipiiriin kuuluvaksi katsotaan:</strong>"
       "<ul><li>Poliittisesti vaikutusvaltaisen henkilön puoliso tai puolisoon kansallisen "
       "lain mukaisesti rinnastettava kumppani</li>"
       "<li>Poliittisesti vaikutusvaltaisen henkilön lapset sekä heidän puolisonsa tai puolisoon "
       "kansallisen lain mukaisesti rinnastettava kumppani.</li>"
       "<li>Poliittisesti vaikutusvaltaisen henkilön vanhemmat</li></ul></p>")}}
    :questions
    (questions-registry/resolve-questions
     [[:kyc-person/PEP? mandatory-field-meta]
      [:kyc-person/pep-edn {:data/template    pep/schema
                            :mandatory-field? (fn [answers]
                                                (= :yes (:kyc-person/PEP? answers)))}]])}

   ; Removed as per ticket DEV-5104
   #_:kyc.natural-person/investor-categorization
   #_{:ord          3.0
      :translations {:en "Investor categorization"
                     :fi "Sijoittajaluokitus"}
      :questions
      (questions-registry/resolve-questions
       [[:kyc-person/professional-investor?]
        [:kyc-person/professional-investor-criterias]
        [:kyc-person/professional-investor-notes]
        [:kyc-person/non-professional-investor-notes]])}
   :kyc.natural-person/tax-reporting-information
   {:ord          3.0
    :translations {:en "Tax reporting information"
                   :fi "Verotiedot"}
    :questions    (questions-registry/resolve-questions
                   [[:kyc-person/tax-countries mandatory-field-meta]
                    [:kyc-person/usa-tax-residency mandatory-field-meta]])}

   :kyc.natural-person/sanctions
   {:ord          3.5
    :translations {:en "International sanctions"
                   :fi "Kansainväliset pakotteet"}
    :questions    (questions-registry/resolve-questions
                   [[:kyc-person/capman-sanctions-confirmation mandatory-field-meta]
                    [:kyc-person/capman-sanctions-cannot-confirm-clarification]])}

   :kyc.natural-person/additional-documents
   {:ord
    4.0

    :translations
    {:en "Additional documents"
     :fi "Liitetiedostot"}

    :keep-together?
    true

    :info
    {:en ""
     :fi ""}

    :questions
    (questions-registry/resolve-questions
     [[:kyc-person/number-of-documents]
      [:kyc-person/document-1]
      [:kyc-person/document-2]
      [:kyc-person/document-3]
      [:kyc-person/document-4]
      [:kyc-person/document-5]])}

   :kyc.natural-person/identification
   {:ord          5.0
    :translations {:en "Identification"
                   :fi "Tunnistetiedot"}
    :info
    {:en (str "<p>True copies must be certified by any third person.</p>"
              "<p>The certification should contain certifier's name, occupation or education, "
              "phone number, email address, date, and signature.</p>")
     :fi (str "<p>Asiakirjojen tulee oikeaksi todistaa kuka tahansa kolmas henkilö.</p>"
              "<p>Todistuksen tulee sisältää oikeaksi todistajan nimi, ammatti tai koulutus, "
              "puhelinnumero, sähköpostiosoite, päivämäärä ja allekirjoitus.</p>")}
    :questions
    (questions-registry/resolve-questions
     (let [old-id-filled-fn (fn [answers _]
                              (-> answers
                                  :kyc-person/identification-attachment))]
       (concat
        [[:kyc-person/signer-email mandatory-field-meta]
         [:kyc-person/type-of-identification-attachment
          {:mandatory-field? true
           :options          capman-signer-identification-options}]
         [:kyc-person/identification-expires mandatory-field-meta]

         ;; removed in DEV-7881
         [:kyc-person/identification-attachment
          {:conditional-field old-id-filled-fn
           :skip-previous-answers-copy? true
           :mandatory-field?  old-id-filled-fn}]]

        (let [condition-fn (fn [answers kyc-process]
                             (and (not (old-id-filled-fn answers kyc-process))
                                  (-> answers
                                      :kyc-person/type-of-identification-attachment
                                      (= :identification.type/passport))))]
          [[:kyc-person/passport-photo-page-file
            {:conditional-field condition-fn
             :mandatory-field?  condition-fn
             :translations {:en "Copy of certified passport"
                            :fi "Oikeaksi todistettu kopio passista"}}]])

        (let [condition-fn (fn [answers kyc-process]
                             (and (not (old-id-filled-fn answers kyc-process))
                                  (-> answers
                                      :kyc-person/type-of-identification-attachment
                                      (= :identification.type/id-card))))]
          [[:kyc-person/national-id-card-front-side-file
            {:conditional-field condition-fn
             :mandatory-field?  condition-fn
             :translations {:en "Copy of certified national ID card (front side)"
                            :fi "Oikeaksi todistettu kopio henkilökortista (etuosa)"}}]
           [:kyc-person/national-id-card-back-side-file
            {:conditional-field condition-fn
             :mandatory-field?  condition-fn
             :translations {:en "Copy of certified national ID card (back side)"
                            :fi "Oikeaksi todistettu kopio henkilökortista (takaosa)"}}]]))))}

   :kyc.natural-person/signature-info
   {:ord
    7.0
    :translations
    {:en ""
     :fi ""}

    :end-of-pdf?
    true

    :page-break-before?
    true

    :conditional-block
    (fn [_answers kyc-process]
      (some #{(:kyc-process/status kyc-process)}
            [:kyc-process.status/signatures
             :kyc-process.status/waiting-for-e-sign]))

    :questions
    (questions-registry/resolve-questions
     [[:kyc-person/signature-confirmation
       {:data/type       :data.type/info
        :optional-field? true
        :translations
        {:en
         (str "On behalf of the investor, I hereby confirm that the information I have "
              "declared in this form and in its appendices are true and correct. "
              "I undertake to inform CapMan (kyc@capman.com) within 30 days in case of "
              "any changes to the declared information.")
         :fi
         (str "Vakuutan, että tässä lomakkeessa ja sen liitteissä olevat tiedot ovat oikeita, "
              "ja että sijoittaja sitoutuu ilmoittamaan näiden tietojen muutoksista "
              "CapManille (kyc@capman.com) 30 päivän kuluessa muutoksesta.")}}]])}

   :kyc.natural-person/signature
   {:ord
    8.0
    :translations
    {:en "Signatures"
     :fi "Allekirjoitukset"}

    :keep-together?
    true

    :end-of-pdf?
    true

    :conditional-block
    (fn [_answers kyc-process]
      (some #{(:kyc-process/status kyc-process)}
            [:kyc-process.status/signatures
             :kyc-process.status/waiting-for-e-sign]))
    :section-not-read-only
    (fn [_answers kyc-process]
      (some #{(:kyc-process/status kyc-process)}
            [:kyc-process.status/signatures
             :kyc-process.status/waiting-for-e-sign]))
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/signature-place
       {:conditional-field (fn [_answers kyc-process]
                             (some #{(:kyc-process/status kyc-process)}
                                   [:kyc-process.status/signatures
                                    :kyc-process.status/waiting-for-e-sign]))}]
      [:kyc-legal-entity/signature-date
       {:conditional-field (fn [_answers kyc-process]
                             (some #{(:kyc-process/status kyc-process)}
                                   [:kyc-process.status/signatures
                                    :kyc-process.status/waiting-for-e-sign]))}]
      [:kyc-legal-entity/signature-name
       {:conditional-field (fn [_answers kyc-process]
                             (some #{(:kyc-process/status kyc-process)}
                                   [:kyc-process.status/signatures
                                    :kyc-process.status/waiting-for-e-sign]))}]
      [:kyc-legal-entity/signature-signature
       {:conditional-field (fn [_answers kyc-process]
                             (some #{(:kyc-process/status kyc-process)}
                                   [:kyc-process.status/signatures
                                    :kyc-process.status/waiting-for-e-sign]))}]])}})
